import React from "react";
import { X, MapPin, Briefcase, Ruler, Heart, Home } from "lucide-react";
import { PhotoImg, VerifiedBadge, OfferChip } from "./common";
import { formatHeight } from "./HeightInput";

function Tags({ title, items, accent }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((i) => (
          <span key={i} className={`rounded-full px-3 py-1 text-sm ${accent ? "bg-[#541224]/40 border border-[#D4AF37]/20 text-[#FBF8F3]" : "bg-[#1C171A] border border-white/8 text-[#C9A96E]"}`}>{i}</span>
        ))}
      </div>
    </div>
  );
}

export function ProfilePreview({ form, photos = [], mainPhotoId, marketLabel, age, selfieVerified, idVerified, onClose }) {
  const main = mainPhotoId || photos[0]?.photo_id;
  const intentions = [form.primary_intention, ...(form.dating_intentions || [])].filter(Boolean);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75" data-testid="profile-preview-modal">
      <div className="w-full max-w-3xl rounded-2xl border border-[#D4AF37]/25 bg-[#141113] max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#141113]/95 backdrop-blur px-5 py-3.5 border-b border-white/10 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Preview</div>
            <h3 className="font-serif text-lg text-[#FBF8F3]">How others see you</h3>
          </div>
          <button data-testid="preview-close-btn" onClick={onClose} className="text-[#9E978E] hover:text-[#FBF8F3]"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 p-5">
          <div>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
              <PhotoImg photoId={main} alt={form.display_name} fallbackInitial={form.display_name?.[0]} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3"><VerifiedBadge selfie={selfieVerified} id={idVerified} size="md" /></div>
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {photos.map((ph) => <div key={ph.photo_id} className="h-14 w-14 rounded-lg overflow-hidden border border-white/10 shrink-0"><PhotoImg photoId={ph.photo_id} className="w-full h-full object-cover" /></div>)}
              </div>
            )}
          </div>
          <div>
            <h1 className="font-serif text-3xl text-[#FBF8F3] leading-tight">{form.display_name || "Your name"}{age ? `, ${age}` : ""}</h1>
            {marketLabel && <p className="text-[#D8D2C9] flex items-center gap-1.5 mt-1 text-sm"><MapPin size={14} />{marketLabel}</p>}
            {form.headline && <p className="text-[#C9A96E] italic font-serif text-lg mt-2">"{form.headline}"</p>}
            <div className="mt-4"><OfferChip amount={form.preferred_offer || null} /></div>
            {form.about_me && (
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-1.5">About</div>
                <p className="text-[#D8D2C9] leading-relaxed text-sm">{form.about_me}</p>
              </div>
            )}
            <div className="mt-5 space-y-2">
              {form.occupation && <div className="flex items-center gap-3 text-sm text-[#FBF8F3]"><Briefcase size={15} className="text-[#D4AF37]" /> {form.occupation}</div>}
              {form.height_cm && <div className="flex items-center gap-3 text-sm text-[#FBF8F3]"><Ruler size={15} className="text-[#D4AF37]" /> {formatHeight(form.height_cm)}</div>}
            </div>
            <Tags title="Looking for" items={intentions} accent />
            <Tags title="Ideal Date" items={form.date_preferences} accent />
            <Tags title="Interests" items={form.interests} />
            <Tags title="Hobbies" items={form.hobbies} />
          </div>
        </div>
      </div>
    </div>
  );
}
